import * as L from 'leaflet';
import { locatedIconSpec, LOCATED_COLOR } from '../models/located.model';
import { LocatedVesselEntry, LocatedVesselRecord } from '../contracts/vessel.types';
import { AisMapContext } from '../contracts/ais-map.context';

/**
 * What this module must be TOLD rather than know (rules R2/R5 — no sibling
 * imports in map-core, no provider imports in shared):
 */
export interface LocatedLayerOptions {
  /**
   * The entry map the STORE half owns (`located.store.ts`) — this layer
   * paints out of it and writes the entries `upsertLocatedMarker` creates.
   * A thunk so the two halves are wired by the facade, never to each other.
   */
  vessels: () => Map<string, LocatedVesselEntry>;
  /**
   * An amber marker was clicked. The selection-taking that follows (guards,
   * the located selection's colour, the card) is facade glue until phase 5 —
   * `selectLocated(mmsi, { openPopup: true })` today.
   */
  onMarkerClick: (mmsi: string) => void;
}

/**
 * LOCATED VESSELS — the LAYER half: the amber markers, the ring-vs-diamond
 * twin logic against the drawn snapshot, and the labels that follow the
 * snapshot-wide toggle. Extracted verbatim from `VoyageMapController`
 * (phase 4, REFACTOR-PLAN.md §7); peers are reached through `ctx` ports only.
 *
 * Its OWN layer above the snapshot, and deliberately so: the type filter, the
 * clustering pass and the track dim all walk the snapshot layers only, so a
 * vessel the user explicitly searched for can never be filtered out, absorbed
 * into a count bubble, or faded by a rule they did not set. The exemption is
 * structural, not a flag anyone can forget to check (invariant I8).
 */
export class LocatedLayer {
  /** Layer holding located/kept vessel markers (see the class doc — I8). */
  private _locatedLayer: any = null;

  /**
   * How close a located fix has to be to the snapshot's own fix for the two to
   * be the SAME fix rather than two facts about one hull.
   *
   * Metres, not screen pixels, on purpose: a pixel threshold would flip the
   * marker's form on every zoom, so a vessel would grow and lose its diamond
   * under a gesture that changed nothing about the data. In metres the answer
   * is a property of the two fixes, and it only changes when one of them does.
   * Generous enough to absorb float rounding and a moored hull's drift between
   * an AOI pull and a by-MMSI lookup.
   */
  private static readonly TWIN_FIX_METERS = 120;

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: LocatedLayerOptions,
  ) {}

  /** Create or update the amber marker for a located vessel. */
  upsertLocatedMarker(record: LocatedVesselRecord): void {
    if (record.lat == null || record.lng == null) return;
    const map = this.ctx.host.map;
    if (!map) return;
    if (!this._locatedLayer || !map.hasLayer(this._locatedLayer)) {
      this._locatedLayer = L.layerGroup().addTo(map);
    }
    const existing = this.opts.vessels().get(record.mmsi);
    if (existing) {
      // Same hull located again: newer fix wins, but kept-ness survives.
      record.kept = existing.record.kept || record.kept;
      existing.record = record;
      existing.marker.setLatLng([record.lat, record.lng]);
      // The fix may have just moved off the snapshot's — a lookup returning a
      // newer position is exactly how a twin stops being one — so the form is
      // re-decided here rather than only in the display pass.
      this.paintLocated(existing, !!this.snapshotTwinFor(record));
      return;
    }
    const ring = !!this.snapshotTwinFor(record);
    const marker = L.marker([record.lat, record.lng], {
      icon: this.locatedIcon(record, ring),
      keyboard: false,
    } as any);
    marker.on('click', () => this.opts.onMarkerClick(record.mmsi));
    this._locatedLayer.addLayer(marker);
    const entry = { record, marker, ring };
    this.opts.vessels().set(record.mmsi, entry);
    this.paintLocated(entry, ring);
  }

  /** Take one marker off the group — the store's exit path for an entry. */
  removeMarker(marker: any): void {
    if (marker && this._locatedLayer) this._locatedLayer.removeLayer(marker);
  }

  /**
   * The painted snapshot marker standing for this hull at (near enough) the
   * same fix, or null.
   *
   * Only a marker the display pipeline actually PAINTED counts: a hull filtered
   * out by ship type, absorbed into a count bubble, or belonging to a layer
   * playback has taken off the map is not on screen to collide with, and a
   * saved vessel reduced to a ring around nothing would be invisible.
   */
  private snapshotTwinFor(record: LocatedVesselRecord): any | null {
    const lat = record?.lat;
    const lng = record?.lng;
    if (lat == null || lng == null) return null;
    const map = this.ctx.host.map;
    const layer = this.ctx.display.displayLayer;
    if (!map || !layer || !map.hasLayer(layer)) return null;
    const marker = this.ctx.selection.drawnMarkerFor(layer, record.mmsi);
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    if (!latlng) return null;
    const apart = map.distance(latlng, L.latLng(lat, lng));
    return apart <= LocatedLayer.TWIN_FIX_METERS ? marker : null;
  }

  /**
   * Draws one located marker in the form its surroundings call for.
   *
   * A saved hull that the snapshot is ALREADY drawing keeps only its amber
   * ring, painted under the snapshot's own arrow: one vessel, one glyph, with a
   * ring around it saying it is saved. Stacking a second icon on the first is
   * what this replaces — at one position, two markers for one hull read as two
   * vessels, and the arrow underneath (which carries the heading and the ship
   * type) was the one being covered up.
   *
   * Anywhere else — open water, a hull the snapshot is not drawing, or a fix
   * that a lookup has since moved away from the snapshot's — the full diamond
   * is correct and comes back, because there is then nothing underneath it and
   * a ring alone would mark a vessel with no vessel in it.
   */
  private paintLocated(entry: LocatedVesselEntry, ring: boolean): void {
    entry.ring = ring;
    entry.marker.setIcon(this.locatedIcon(entry.record, ring));
    // Under the arrow, not over it. Both markers sit in Leaflet's shared marker
    // pane, where stacking otherwise falls out of latitude — which would put the
    // ring on top of the hull it is supposed to frame about half the time.
    entry.marker.setZIndexOffset?.(ring ? -1000 : 0);
    if (ring) {
      // The snapshot layer names the hulls it draws (`applyVesselLabels`), so a
      // permanent tooltip here would print the same name twice at one point.
      entry.marker.unbindTooltip?.();
    } else if (this.ctx.display.labelsOn) {
      this.bindLocatedLabel(entry);
    }
  }

  /**
   * Re-decides every located marker's form against what the snapshot is drawing
   * right now. Runs at the end of the display pass, so a type filter, a cluster
   * forming, a playback frame or a pan that changes which hulls are painted
   * also settles the markers that sit on top of them.
   */
  syncLocatedTwins(): void {
    const vessels = this.opts.vessels();
    if (!vessels.size) return;
    for (const entry of vessels.values()) {
      const ring = !!this.snapshotTwinFor(entry.record);
      if (ring !== entry.ring) this.paintLocated(entry, ring);
    }
  }

  /**
   * The amber marker's icon. Every decision — arrow vs diamond, the AIS
   * sentinels, the stale-fix hollowing, the ring form — is made pure in
   * `locatedIconSpec` (ais/shared/models/located.model.ts, with its reasoning);
   * this wrapper only hands the result to Leaflet.
   *
   * Private again since phase 7. It was public to give the glyph specs a seam,
   * and they now drive `locatedIconSpec` itself — which is the better test and
   * leaves this with nothing to expose: one line, and the only part of it that
   * can be wrong is `L.divIcon`'s.
   */
  private locatedIcon(record: LocatedVesselRecord, ring: boolean): any {
    return L.divIcon(locatedIconSpec(record, ring));
  }

  private bindLocatedLabel(entry: { record: LocatedVesselRecord; marker: any }): void {
    const label = entry.record.name || `MMSI ${entry.record.mmsi}`;
    entry.marker.unbindTooltip?.();
    entry.marker.bindTooltip(label, {
      permanent: true,
      direction: 'right',
      offset: [14, 0],
      className: 'ais-vessel-label',
    });
  }

  /**
   * Located markers follow the snapshot-wide labels toggle — the Tt control
   * every vessel already obeys. No second labels concept: one switch decides
   * whether the map is named, whatever layer a hull arrived on.
   */
  syncLocatedLabels(): void {
    const on = this.ctx.host.playback.snapshot().showLabels === true;
    for (const entry of this.opts.vessels().values()) {
      entry.marker?.unbindTooltip?.();
      // A ring is framing a snapshot hull, and that hull is named by the
      // snapshot's own labelling pass — naming it here too would double it.
      if (on && !entry.ring) this.bindLocatedLabel(entry);
    }
  }

  /** The drawer row's crosshair: recentre the map on a located vessel's fix. */
  centerLocatedVessel(mmsi: string): void {
    const r = this.opts.vessels().get(mmsi)?.record;
    if (r?.lat != null && r?.lng != null) {
      this.ctx.host.map?.flyTo(
        [r.lat, r.lng],
        Math.max(this.ctx.host.map?.getZoom?.() ?? 8, 8),
      );
    }
  }

  /** The same read-only popup every hull gets, fed from the located record. */
  openLocatedInfo(mmsi: string): void {
    const r = this.opts.vessels().get(mmsi)?.record;
    if (!r || r.lat == null || r.lng == null) return;
    this.ctx.popup.openVesselPopupAt(L.latLng(r.lat, r.lng), {
      Name: r.name,
      mmsi: r.mmsi,
      Lat: r.lat,
      Lng: r.lng,
      shipType: r.shipType,
      navStatus: r.navStatus,
      imo: r.imoNumber,
      callSign: r.callSign,
      flag: r.flag,
      country: r.country,
      time_utc: r.timeUtc,
      Speed: r.sog ?? null,
      Course: r.cog ?? null,
      heading: r.heading ?? null,
      length: r.length ?? null,
      beam: r.beam ?? null,
      destination: r.destination ?? null,
      fromPort: r.fromPort ?? null,
      maxDraught: r.maxDraught ?? null,
      Color: LOCATED_COLOR,
    });
  }

  /**
   * Nulls the layer reference WITHOUT touching the map — for the task-switch
   * reset, which runs inside a shared sweep that has already taken every layer
   * off (invariant I1: a removeLayer here would operate on a map that no
   * longer holds it). The entries themselves are the store's to forget.
   */
  dropRefs(): void {
    this._locatedLayer = null;
  }
}
